import type { PantryCategory, PantryItem, PantryLocation } from '@/types';
import { CATEGORY_OPTIONS, LOCATION_OPTIONS } from './pantry-constants';

export function toIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function addDays(dateString: string, days: number): string {
  const date = new Date(`${dateString}T12:00:00`);
  date.setDate(date.getDate() + days);
  return toIsoDate(date);
}

export function normalizeName(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

export function formatDisplayName(value: string): string {
  return value
    .trim()
    .split(/\s+/)
    .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
    .join(' ');
}

export function parseNonNegative(value: string, fallback: number): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return fallback;
  return parsed;
}

/**
 * Format a numeric amount, preferring common kitchen fractions for
 * readability (0.25 → ¼, 1.5 → 1½, etc). Mixed numbers are rendered
 * as "{integer}{fraction}" (e.g. "1½").
 */
export function formatAmount(value: number): string {
  if (!Number.isFinite(value)) return '0';
  const rounded = Math.round(value * 100) / 100;
  if (rounded === 0) return '0';

  const sign = rounded < 0 ? '-' : '';
  const abs = Math.abs(rounded);
  const whole = Math.floor(abs);
  const frac = abs - whole;

  // Snap common kitchen fractions to unicode glyphs.
  // Tolerance accounts for floating-point + user rounding (e.g. 0.33 ≈ 1/3).
  const fractionMap: Array<[number, string]> = [
    [1 / 8, '⅛'],
    [1 / 4, '¼'],
    [1 / 3, '⅓'],
    [3 / 8, '⅜'],
    [1 / 2, '½'],
    [5 / 8, '⅝'],
    [2 / 3, '⅔'],
    [3 / 4, '¾'],
    [7 / 8, '⅞'],
  ];
  const TOLERANCE = 0.02;
  const match = fractionMap.find(([target]) => Math.abs(frac - target) <= TOLERANCE);

  if (match) {
    if (whole === 0) return `${sign}${match[1]}`;
    return `${sign}${whole}${match[1]}`;
  }

  // No fraction match — return a clean decimal string (no trailing zeros).
  return Number.isInteger(rounded) ? `${sign}${whole}` : `${sign}${abs}`;
}

export function getDaysUntil(expiry: string | undefined): number | null {
  if (!expiry) return null;
  const today = new Date(`${toIsoDate(new Date())}T00:00:00`).getTime();
  const target = new Date(`${expiry}T00:00:00`).getTime();
  if (!Number.isFinite(target)) return null;
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
}

export function formatDateShort(isoDate: string): string {
  return new Date(`${isoDate}T12:00:00`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

export function getCategoryLabel(category: PantryCategory): string {
  return CATEGORY_OPTIONS.find((option) => option.value === category)?.label ?? 'Other';
}

export function getLocationLabel(location: PantryLocation): string {
  return LOCATION_OPTIONS.find((option) => option.value === location)?.label ?? 'Other';
}

export function guessCategory(name: string): PantryCategory {
  const text = normalizeName(name);
  if (/(milk|cheese|yogurt|butter|cream)/.test(text)) return 'dairy';
  if (/(chicken|beef|pork|fish|salmon|turkey|egg)/.test(text)) return 'protein';
  if (/(rice|pasta|oat|quinoa|bread|flour)/.test(text)) return 'grains';
  if (/(pepper|paprika|cumin|turmeric|oregano|basil|salt)/.test(text)) return 'spices';
  if (/(tomato sauce|soy sauce|ketchup|mustard|mayo|vinegar|oil)/.test(text)) return 'condiments';
  if (/(bean|lentil|chickpea|corn|tuna can)/.test(text)) return 'canned';
  if (/(frozen|ice cream)/.test(text)) return 'frozen';
  if (/(apple|banana|lettuce|spinach|onion|garlic|potato|carrot|broccoli)/.test(text)) return 'produce';
  if (/(soda|coffee|tea|juice)/.test(text)) return 'beverages';
  return 'other';
}

export function guessLocation(category: PantryCategory): PantryLocation {
  if (category === 'dairy' || category === 'produce' || category === 'protein') return 'fridge';
  if (category === 'frozen') return 'freezer';
  return 'pantry';
}

export function getItemFlags(item: PantryItem) {
  const days = getDaysUntil(item.expiresAt);
  const isOut = item.amount === 0;
  const isLow = !isOut && item.amount <= item.lowStockThreshold;
  const isExpired = days !== null && days < 0;
  const isExpiringSoon = days !== null && days >= 0 && days <= 3;
  return { isOut, isLow, isExpired, isExpiringSoon, daysUntilExpiry: days };
}

function toIcsUtc(date: Date): string {
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
}

function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');
}

export function buildReminderCalendar(events: Array<{ item: PantryItem; reminderDate: string }>): string {
  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Recipe Sense//Pantry Reminders//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
  ];
  const stamp = toIcsUtc(new Date());
  events.forEach(({ item, reminderDate }) => {
    const start = new Date(`${reminderDate}T09:00:00`);
    const end = new Date(`${reminderDate}T09:30:00`);
    lines.push('BEGIN:VEVENT');
    lines.push(`UID:${escapeIcsText(`${item.id}-${reminderDate}@recipesense`)}`);
    lines.push(`DTSTAMP:${stamp}`);
    lines.push(`DTSTART:${toIcsUtc(start)}`);
    lines.push(`DTEND:${toIcsUtc(end)}`);
    lines.push(`SUMMARY:${escapeIcsText(`Pantry reminder: ${item.ingredientName}`)}`);
    lines.push(
      `DESCRIPTION:${escapeIcsText(
        `Check stock for ${item.ingredientName}${item.expiresAt ? ` (expires ${item.expiresAt})` : ''}.`
      )}`
    );
    lines.push('END:VEVENT');
  });
  lines.push('END:VCALENDAR');
  return lines.join('\r\n');
}
